// Story 11.3 — Mapa `lineNumber → stepId` derivado del YAML del editor.
// Implementación pure-text scanner: el formato canónico del editor (dump
// de `yaml@^2` lib) es predecible. NO usa yaml lib para evitar acoplar al
// runtime — los tests corren sin deps.
//
// Heurística: localizamos el bloque `steps:` (o `<flowName>: { steps: ... }`),
// luego cada línea que empiece con `- id:` (o `-` y los siguientes campos)
// inicia un nuevo step. El step "ocupa" todas las líneas hasta el siguiente
// item del array al mismo indent, o el fin del bloque steps.

export interface LineMapResult {
  /** lineNumber (1-based) → stepId. */
  lineToStep: Map<number, string>;
  /** stepId → línea de inicio (1-based) del step. */
  stepToLine: Map<string, number>;
  /** True si el scanner detectó un bloque steps; false para YAML sin steps. */
  ok: boolean;
}

const EMPTY: LineMapResult = {
  lineToStep: new Map(),
  stepToLine: new Map(),
  ok: false,
};

/**
 * Construye el line map a partir del YAML. Si no se detecta un bloque
 * `steps:`, retorna mapas vacíos con `ok=false`. El caller debe mantener
 * el último mapa válido durante typing inválido.
 */
export function buildLineMap(content: string): LineMapResult {
  if (!content || content.trim().length === 0) return EMPTY;
  const lines = content.split('\n');

  // 1. Detectar la línea de `steps:` y su indentación.
  const stepsHeader = findStepsHeader(lines);
  if (!stepsHeader) return EMPTY;

  const { lineIndex: stepsLine, indent: stepsIndent } = stepsHeader;
  const itemIndent = stepsIndent + 2; // YAML standard: 2 spaces más que el padre

  // 2. Recorrer las líneas a partir de `steps:` y dividir en items.
  // Un item empieza en una línea cuya indentación es `itemIndent` y empieza
  // con `- ` (o `-\n` para items cuyo primer field viene en la siguiente línea).
  const items: Array<{ start: number; end: number; id: string | null }> = [];
  let currentItem: { start: number; firstFieldLines: string[]; idAt: number | null } | null = null;
  let cursor = stepsLine + 1;

  while (cursor < lines.length) {
    const raw = lines[cursor] ?? '';
    const trimmed = raw.trim();

    // Línea en blanco o comentario: pertenece al item activo (si existe).
    if (trimmed === '' || trimmed.startsWith('#')) {
      cursor++;
      continue;
    }

    const indent = countIndent(raw);

    // Si la indentación cae por debajo de `itemIndent`, salimos del bloque steps.
    if (indent < itemIndent) {
      break;
    }

    // Inicio de un nuevo item: indent === itemIndent y empieza con `-`.
    if (indent === itemIndent && trimmed.startsWith('-')) {
      // Cerrar el item previo.
      if (currentItem) {
        items.push({
          start: currentItem.start,
          end: cursor - 1,
          id: extractIdFromBlock(currentItem.firstFieldLines),
        });
      }
      currentItem = {
        start: cursor + 1, // 1-based
        firstFieldLines: [stripDashPrefix(raw)],
        idAt: null,
      };
    } else if (currentItem) {
      // Línea siguiente del item activo — solo guardamos las primeras pocas
      // (el id suele estar en las primeras 1-3 líneas tras el `-`).
      if (currentItem.firstFieldLines.length < 8) {
        currentItem.firstFieldLines.push(raw);
      }
    }
    cursor++;
  }
  // Cerrar el último item.
  if (currentItem) {
    items.push({
      start: currentItem.start,
      end: cursor - 1,
      id: extractIdFromBlock(currentItem.firstFieldLines),
    });
  }

  if (items.length === 0) return EMPTY;

  const lineToStep = new Map<number, string>();
  const stepToLine = new Map<string, number>();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const stepId = item.id || `step-${i}`;
    if (!stepToLine.has(stepId)) stepToLine.set(stepId, item.start);
    // El `+1` compensa la mezcla 0-based (cursor) ↔ 1-based (item.end).
    // El P-409 del review fue un análisis incorrecto: los tests existentes
    // demuestran que `[item.start, item.end+1]` es el rango canónico
    // intencional. Mantenemos el comportamiento.
    for (let l = item.start; l <= item.end + 1; l++) {
      // Solo pisamos si la línea no fue asignada ya (los duplicados serían
      // hijos anidados que prefieren el item directo padre — first wins).
      if (!lineToStep.has(l)) lineToStep.set(l, stepId);
    }
  }
  return { lineToStep, stepToLine, ok: true };
}

function findStepsHeader(lines: string[]): { lineIndex: number; indent: number } | null {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();
    if (trimmed === 'steps:' || /^steps:\s*$/.test(trimmed) || /^steps:\s*\[\]$/.test(trimmed)) {
      return { lineIndex: i, indent: countIndent(line) };
    }
  }
  return null;
}

function countIndent(line: string): number {
  let n = 0;
  while (n < line.length && line[n] === ' ') n++;
  return n;
}

function stripDashPrefix(line: string): string {
  // "  - id: foo" → "id: foo"
  const idx = line.indexOf('-');
  if (idx < 0) return line;
  // Quita el `-` y los espacios subsiguientes.
  return line.slice(idx + 1).trimStart();
}

function extractIdFromBlock(blockLines: string[]): string | null {
  const idRegex = /^id:\s*(['"]?)([\w-]+)\1\s*$/;
  for (const raw of blockLines) {
    const trimmed = raw.trim();
    const m = idRegex.exec(trimmed);
    if (m) return m[2] ?? null;
  }
  return null;
}
